import type { Options } from 'highcharts';
import { DeepPartial } from "../../types";

/** resolved --og-* variable of the element, undefined when not defined */
export const getThemeColor = (host: Element, name: string): string =>
	getComputedStyle(host).getPropertyValue(`--og-${name}`).trim() || undefined;

/** #rrggbb or rgb() color with an alpha channel applied, other formats returned as they are */
export const applyAlpha = (color: string, alpha: number): string => {
	const hex = /^#([0-9A-Fa-f]{6})$/.exec(color ?? '');
	if (hex) {
		const value = parseInt(hex[1], 16);
		return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
	}
	// themes resolve the variables in the rgb() form too
	const rgb = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/.exec(color ?? '');
	if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;
	return color;
};

/**
 * Highcharts theme built from the resolved --og-highcharts-* variables of the
 * host element. Colors are painted as SVG presentation attributes where CSS
 * variables do not resolve, so the values have to be read from the computed
 * style and passed as plain options. Covers the themable subset of the former
 * styled mode stylesheet used by the rendered chart types; the values are
 * only read here - the host re-renders on a theme change.
 */
export const getChartTheme = (host: Element): DeepPartial<Options> => {
	const style = getComputedStyle(host);
	const v = (name: string) => style.getPropertyValue(`--og-${name}`).trim() || undefined;

	const background = v('highcharts-background-color');
	const highlight80 = v('highcharts-highlight-color-80');
	const neutral100 = v('highcharts-neutral-color-100');
	const neutral80 = v('highcharts-neutral-color-80');
	const neutral60 = v('highcharts-neutral-color-60');
	const neutral20 = v('highcharts-neutral-color-20');
	const neutral10 = v('highcharts-neutral-color-10');
	const neutral5 = v('highcharts-neutral-color-5');
	const fontSize = v('font-size');

	const axis = () => ({
		labels: {style: {color: neutral80, fontSize}},
		title: {style: {color: neutral60, fontSize}},
		gridLineColor: neutral10,
		minorGridLineColor: neutral5,
		lineColor: neutral80,
		tickColor: neutral80,
	});

	return {
		colors: Array.from({length: 10}, (_, i) => v(`highcharts-color-${i}`)).filter(Boolean),
		chart: {
			backgroundColor: background,
			style: {fontFamily: v('font-family'), fontSize},
			// drag to zoom selection overlay
			selectionMarkerFill: highlight80 ? applyAlpha(highlight80, 0.25) : undefined,
			resetZoomButton: {
				theme: {
					fill: background,
					stroke: neutral20,
					r: 4,
					style: {color: neutral80, fontSize},
					states: {hover: {
						fill: neutral10,
						style: {color: neutral100},
					}},
				},
			},
		},
		title: {style: {color: neutral80, fontSize: v('highcharts-title-font-size')}},
		subtitle: {style: {color: neutral60, fontSize: v('highcharts-subtitle-font-size')}},
		xAxis: axis(),
		yAxis: axis(),
		legend: {
			itemStyle: {color: neutral80, fontSize, fontWeight: 'normal'},
			itemHoverStyle: {color: neutral100},
			itemHiddenStyle: {color: neutral60, textDecoration: 'line-through'},
		},
		tooltip: {
			backgroundColor: background,
			borderWidth: 0,
			style: {color: neutral80, fontSize},
		},
		plotOptions: {
			series: {
				// the stroke between column/pie points and around the markers
				borderColor: background,
				marker: {lineColor: background},
				dataLabels: {style: {color: neutral80, textOutline: 'none'}},
			},
			gauge: {
				dial: {backgroundColor: neutral100, borderWidth: 0},
				pivot: {backgroundColor: 'transparent'},
				dataLabels: {borderColor: neutral20, borderWidth: 1},
			},
		},
	};
};

/** default fill of a plot band without an own color (the former theme fill) */
export const getPlotBandColor = (host: Element): string =>
	applyAlpha(getThemeColor(host, 'highcharts-neutral-color-100') ?? '#000000', 0.05);

/** plot band label color (defaults to a fixed gray otherwise) */
export const getPlotBandLabelStyle = (host: Element): Record<string, string> => ({
	color: getThemeColor(host, 'highcharts-neutral-color-80'),
});

/** background/border of the data label box shown with a shape set (see applySeries) */
export const getDataLabelShapeStyle = (host: Element) => {
	const background = getThemeColor(host, 'highcharts-background-color');
	return {
		backgroundColor: background ? applyAlpha(background, 0.85) : undefined,
		borderColor: getThemeColor(host, 'highcharts-neutral-color-20'),
		borderWidth: 1,
	};
};
